import { useCallback, useEffect, useState } from "react";
import SpotifyWebApi from "spotify-web-api-js";
import { useTokenContext } from "src/auth/UserTokenProvider";
import { useAlbumContext } from "src/context/AlbumContext";
import { requestPlaybackSync } from "src/music/playbackSync";
import { SongUtil } from "src/util/SongUtil";

const spotifyApi = new SpotifyWebApi();

export interface Track {
  id: string;
  name: string;
  artists: string[];
  coverUrl: string;
  popularity: number;
  liked: boolean;
}

const cache: { [key: string]: Track[] } = {};

export type { QueuedTrack } from "src/context/AlbumContext";

export const useSpotifyTracks = () => {
  const { token } = useTokenContext();
  const [loading, setLoading] = useState(false);
  const {
    isStickingToAlbum,
    stickToAlbum,
    resumeOriginalPlaylist,
    clearUpQueue,
    pendingTracks,
    lastQueuedTrack,
    queueError
  } = useAlbumContext();

  useEffect(() => {
    if (token?.value) {
      spotifyApi.setAccessToken(token.value);
    }
  }, [token?.value]);

  const searchTopTracks = useCallback(
    async (currentTrackName: string, artistName: string): Promise<Track[]> => {
      const filter = (track: Track) =>
        track.artists.includes(artistName) &&
        !SongUtil.sameSong(track, currentTrackName, artistName);

      const searchFunction = (trackName: string) => spotifyApi.searchTracks(`${artistName}`);

      return doSearchTracks(currentTrackName, artistName, searchFunction, filter);
    },
    []
  );

  const searchLiveTracks = useCallback(
    async (currentTrackName: string, artistName: string): Promise<Track[]> => {
      const filter = (track: Track) =>
        track.name.toLowerCase().includes("live") &&
        track.name.toLowerCase().includes(currentTrackName.toLowerCase()) &&
        track.artists.includes(artistName) &&
        !SongUtil.sameSong(track, currentTrackName, artistName);

      const searchFunction = (trackName: string) =>
        spotifyApi.searchTracks(`${currentTrackName} ${artistName} live`);

      return doSearchTracks(currentTrackName, artistName, searchFunction, filter, "live");
    },
    []
  );

  const searchCoverTracks = useCallback(
    async (currentTrackName: string, artistName: string): Promise<Track[]> => {
      const filter = (track: Track) =>
        track.name.toLowerCase().includes("cover") &&
        track.name.toLowerCase().includes(currentTrackName.toLowerCase()) &&
        !track.artists.includes(artistName);

      const searchFunction = (trackName: string) =>
        spotifyApi.searchTracks(`${currentTrackName} cover`);

      return doSearchTracks(currentTrackName, artistName, searchFunction, filter, "cover");
    },
    []
  );

  const doSearchTracks = async (
    currentTrackName: string,
    artistName: string,
    searchFunction: (trackName: string) => Promise<SpotifyApi.TrackSearchResponse>,
    cbFilter: (track: Track) => boolean,
    searchPrefix = ""
  ): Promise<Track[]> => {
    const queryKey = `${currentTrackName}-${artistName}-${searchPrefix}`;

    if (cache[queryKey]) {
      return cache[queryKey];
    }

    setLoading(true);
    try {
      const response = await searchFunction(currentTrackName);
      const uniqueTracks = new Set();
      const tracks = response.tracks.items
        .map((item: any) => ({
          id: item.id,
          name: item.name,
          artists: item.artists.map((artist: any) => artist.name),
          coverUrl: item.album.images[0].url,
          popularity: item.popularity,
          liked: false
        }))
        .filter(track => {
          const identifier = `${track.name}-${track.artists.join(",")}`;
          if (uniqueTracks.has(identifier)) {
            return false;
          } else {
            uniqueTracks.add(identifier);
            return true;
          }
        })
        .sort((a, b) => b.popularity - a.popularity)
        .filter(cbFilter);
      const trackIds = tracks.map(track => track.id);
      if (trackIds.length > 0) {
        const likedTracks = await spotifyApi.containsMySavedTracks(trackIds);
        tracks.forEach((track, index) => {
          track.liked = likedTracks[index];
        });
      }
      cache[queryKey] = tracks;
      return tracks;
    } catch (error) {
      console.error("Error fetching live tracks:", error);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const switchTrack = useCallback(async (trackId: string) => {
    try {
      await spotifyApi.queue(`spotify:track:${trackId}`);
      await spotifyApi.skipToNext();
      requestPlaybackSync();
    } catch (error) {
      console.error("Error switching track:", error);
    }
  }, []);

  const queueTrack = useCallback(async (trackId: string): Promise<boolean> => {
    try {
      await spotifyApi.queue(`spotify:track:${trackId}`);
      return true;
    } catch (error) {
      console.error("Error queuing track:", error);
      return false;
    }
  }, []);

  const playAlbum = useCallback(async (albumUri: string) => {
    try {
      await spotifyApi.play({ context_uri: albumUri });
      requestPlaybackSync();
      return true;
    } catch (error) {
      console.error("Error playing album:", error);
      return false;
    }
  }, []);

  return {
    searchLiveTracks,
    searchCoverTracks,
    searchTopTracks,
    loading,
    switchTrack,
    queueTrack,
    playAlbum,
    stickToAlbum,
    resumeOriginalPlaylist,
    clearUpQueue,
    isStickingToAlbum,
    pendingTracks,
    lastQueuedTrack,
    queueError
  };
};

export default useSpotifyTracks;
